import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Clock, FileClock, History, Search } from 'lucide-react'
import { AppShell } from '@/components/AppShell'
import { FieldLabel } from '@/components/FieldLabel'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { ListPagination } from '@/components/ListPagination'
import { SearchSelect } from '@/components/SearchSelect'
import { VersionColumns } from '@/components/VersionColumns'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardIcon } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useNetwork } from '@/contexts/NetworkContext'
import { useCurrentUser } from '@/contexts/UserContext'
import { apiErrorDescription } from '@/services/core/apiError'
import { AuthService } from '@/services/core/auth'
import {
  RecentObjects,
  VERSION_HISTORY_TYPES,
  VersionHistoryApi,
  type VersionHistoryType,
} from '@/services/shared/versionHistoryApi'

const PAGE_SIZE = 10
const TYPE_OPTIONS = VERSION_HISTORY_TYPES.map((type) => ({ value: type, label: type }))

function isVersionHistoryType(value: string | null): value is VersionHistoryType {
  return !!value && (VERSION_HISTORY_TYPES as readonly string[]).includes(value)
}

/**
 * Every saved version of one object, with what changed between each — the
 * support tool UII has at /version-history. The object comes from the URL, so
 * the support bar can open it on whatever page you were looking at.
 */
export function VersionHistoryPage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { scopeKey, scopeReady } = useNetwork()

  const typeParam = searchParams.get('type')
  const type = isVersionHistoryType(typeParam) ? typeParam : null
  const id = searchParams.get('id')?.trim() ?? ''
  const page = Math.max(1, Number(searchParams.get('page')) || 1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['versionhistory', type, id, page, scopeKey],
    queryFn: () => VersionHistoryApi.getPage(type!, id, page, PAGE_SIZE),
    enabled: AuthService.isAuthenticated() && scopeReady && !!type && !!id,
  })

  const setParams = (next: { type: string; id: string; page?: number }) => {
    const params = new URLSearchParams({ type: next.type, id: next.id })
    if (next.page && next.page > 1) params.set('page', String(next.page))
    setSearchParams(params)
  }

  const versions = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  return (
    <AppShell crumbs={[{ label: t('versionHistory.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('versionHistory.title')}</h1>
            <p>{t('versionHistory.subtitle')}</p>
          </div>
        </div>

        <ObjectForm
          key={`${type}:${id}`}
          initialType={type ?? ''}
          initialId={id}
          onSubmit={(nextType, nextId) => setParams({ type: nextType, id: nextId })}
        />

        {type && id && (
          <ListCard tone="insight">
            <ListCardHeader
              title={
                <>
                  <CardIcon icon={History} />
                  {t('versionHistory.versions')}
                </>
              }
              count={totalCount}
              subtitle={
                <span className="font-mono text-xs">
                  {type} · {id}
                </span>
              }
            />
            <CardContent className="flex flex-col">
              {isLoading && (
                <div className="flex flex-col gap-3">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              )}
              {error && (
                <Empty>
                  <EmptyHeader>
                    <EmptyTitle>{t('versionHistory.loadFailed')}</EmptyTitle>
                    <EmptyDescription>{apiErrorDescription(error)}</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              {!isLoading && !error && versions.length === 0 && (
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <FileClock />
                    </EmptyMedia>
                    <EmptyTitle>{t('versionHistory.noVersions')}</EmptyTitle>
                    <EmptyDescription>{t('versionHistory.noVersionsHint')}</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
              {versions.length > 0 && (
                <VersionColumns
                  versions={versions}
                  newestNumber={totalCount - (page - 1) * PAGE_SIZE}
                />
              )}
            </CardContent>
            <ListPagination
              page={page}
              pageSize={PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={(next) => setParams({ type, id, page: next })}
            />
          </ListCard>
        )}
      </div>
    </AppShell>
  )
}

interface ObjectFormProps {
  initialType: string
  initialId: string
  onSubmit: (type: string, id: string) => void
}

function ObjectForm({ initialType, initialId, onSubmit }: ObjectFormProps) {
  const { t } = useTranslation()
  const currentUser = useCurrentUser()
  const { currentNetwork } = useNetwork()
  const [type, setType] = useState(initialType)
  const [id, setId] = useState(initialId)
  const canSubmit = !!type && !!id.trim()

  // Same scope the app shell records visits under.
  const recent = RecentObjects.list(`${currentUser?.username ?? ''}|${currentNetwork?.tagId ?? ''}`)
  const recentOptions = recent.map((item) => ({
    value: `${item.type}:${item.id}`,
    label: `${item.name} · ${item.type}`,
  }))

  return (
    <Card>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) onSubmit(type, id.trim())
          }}
        >
          <div className="p-field w-full">
            <FieldLabel>{t('versionHistory.recentlyViewed')}</FieldLabel>
            <SearchSelect
              value={recentOptions.some((o) => o.value === `${type}:${id}`) ? `${type}:${id}` : ''}
              onChange={(value) => {
                const item = recent.find((r) => `${r.type}:${r.id}` === value)
                if (item) onSubmit(item.type, item.id)
              }}
              options={recentOptions}
              icon={Clock}
              placeholder={
                recent.length ? t('versionHistory.pickRecent') : t('versionHistory.noRecent')
              }
              searchPlaceholder={t('versionHistory.searchRecent')}
              disabled={!recent.length}
            />
          </div>
          <div className="p-field w-64 max-md:w-full">
            <FieldLabel>{t('versionHistory.objectType')}</FieldLabel>
            <SearchSelect
              value={type}
              onChange={setType}
              options={TYPE_OPTIONS}
              placeholder={t('versionHistory.selectType')}
              searchPlaceholder={t('versionHistory.searchTypes')}
            />
          </div>
          <div className="p-field min-w-64 flex-1">
            <FieldLabel htmlFor="version-history-id">{t('versionHistory.objectId')}</FieldLabel>
            <Input
              id="version-history-id"
              className="font-mono"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder={t('versionHistory.objectIdPlaceholder')}
              autoComplete="off"
            />
          </div>
          <Button type="submit" disabled={!canSubmit}>
            <Search />
            {t('versionHistory.show')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
